import React, { useState } from 'react';
import { useTopups } from '../hooks/useTopups';
import { useAuth } from '../context/AuthContext';
import '../styles/Dashboard.css';

const TopupsPage = () => {
  const { profile } = useAuth();
  const {
    topups,
    loading,
    error,
    pagination,
    createTopup,
    goToPage,
    setPerPage,
  } = useTopups();

  const [showNewTopupModal, setShowNewTopupModal] = useState(false);
  const [newTopupAmount, setNewTopupAmount] = useState(10);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  const handleCreateTopup = async () => {
    if (newTopupAmount < 5) {
      setCreateError('Minimum topup amount is $5');
      return;
    }

    setIsCreating(true);
    setCreateError('');

    const result = await createTopup(newTopupAmount);

    if (result.success) {
      setShowNewTopupModal(false);
      setNewTopupAmount(10);
      if (result.data?.payment_url && !result.data?.isPlaceholder) {
        try {
          const paymentUrl = new URL(result.data.payment_url);
          const trustedDomains = ['cryptomuss.com', 'api.cryptomuss.com'];
          if (paymentUrl.protocol === 'https:' && trustedDomains.some(d => paymentUrl.hostname === d || paymentUrl.hostname.endsWith('.' + d))) {
            window.open(result.data.payment_url, '_blank', 'noopener,noreferrer');
          }
        } catch (e) {
        }
      }
    } else {
      setCreateError(result.error || 'Failed to create topup');
    }

    setIsCreating(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'completed';
      case 'pending':
        return 'pending';
      case 'failed':
      case 'expired':
        return 'failed';
      case 'manual_adjustment':
        return 'manual';
      default:
        return '';
    }
  };

  const formatStatus = (status) => {
    if (status === 'manual_adjustment') return 'Manual Adjustment';
    return status?.charAt(0).toUpperCase() + status?.slice(1) || '';
  };

  const formatPaymentType = (type) => {
    if (type === 'cryptocurrency') return 'Cryptocurrency';
    if (type === 'manual_adjustment') return 'Manual Adjustment';
    if (type === 'referral_bonus') return 'Referral Bonus';
    return type || '';
  };

  return (
    <div className="topups-page">
      <h1 className="page-title">Topups</h1>

      <div className="topups-container">
        <div className="topups-header">
          <button className="new-topup-btn" onClick={() => setShowNewTopupModal(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            New Topup
          </button>
        </div>

        <div className="filter-bar">
          <button className="filter-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46"/>
            </svg>
            <span className="filter-count">0</span>
          </button>
        </div>

        {error && (
          <div className="error-message">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            {error}
          </div>
        )}

        <div className="topups-table-container">
          <table className="topups-table">
            <thead>
              <tr>
                <th>Order id</th>
                <th>Amount <span className="sort-icon">&#9662;</span></th>
                <th>Status <span className="sort-icon">&#9662;</span></th>
                <th>Payment type <span className="sort-icon">&#9662;</span></th>
                <th>Created at <span className="sort-icon">&#9662;</span></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="loading-cell">Loading...</td>
                </tr>
              ) : topups.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty-cell">No topups found</td>
                </tr>
              ) : (
                topups.map((topup) => (
                  <tr key={topup.id}>
                    <td>{topup.order_id}</td>
                    <td>${parseFloat(topup.amount).toFixed(2)}</td>
                    <td>
                      <span className={`status-badge ${getStatusClass(topup.status)}`}>
                        {formatStatus(topup.status)}
                      </span>
                    </td>
                    <td>{formatPaymentType(topup.payment_type)}</td>
                    <td>{formatDate(topup.created_at)}</td>
                    <td>
                      {topup.status === 'pending' && topup.payment_url && (() => {
                        try {
                          const url = new URL(topup.payment_url);
                          const trusted = ['cryptomuss.com', 'api.cryptomuss.com'];
                          if (url.protocol === 'https:' && trusted.some(d => url.hostname === d || url.hostname.endsWith('.' + d))) {
                            return (
                              <a
                                href={topup.payment_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="pay-btn"
                              >
                                Pay
                              </a>
                            );
                          }
                        } catch (e) {}
                        return null;
                      })()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="table-footer">
          <span className="results-info">
            Showing {topups.length > 0 ? 1 : 0} to {topups.length} of {pagination.total} results
          </span>
          <div className="per-page-selector">
            <span>Per page</span>
            <select
              value={pagination.perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>

      {showNewTopupModal && (
        <div className="modal-overlay" onClick={() => setShowNewTopupModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>New Topup</h3>
              <button className="modal-close" onClick={() => setShowNewTopupModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <p className="modal-info">
                Add funds to your account. Minimum topup is $5.00.
              </p>

              <div className="amount-selector">
                <label>Amount (USD)</label>
                <div className="amount-input-group">
                  <span className="currency-symbol">$</span>
                  <input
                    type="number"
                    min="5"
                    step="1"
                    value={newTopupAmount}
                    onChange={(e) => setNewTopupAmount(Number(e.target.value))}
                  />
                </div>
              </div>

              <div className="quick-amounts">
                {[10, 25, 50, 100, 250, 500].map((amount) => (
                  <button
                    key={amount}
                    className={`quick-amount-btn ${newTopupAmount === amount ? 'active' : ''}`}
                    onClick={() => setNewTopupAmount(amount)}
                  >
                    ${amount}
                  </button>
                ))}
              </div>

              {createError && (
                <div className="modal-error">
                  {createError}
                </div>
              )}

              <div className="payment-notice">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
                <span>Payment will be processed via cryptocurrency (Cryptomuss)</span>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="cancel-btn"
                onClick={() => setShowNewTopupModal(false)}
                disabled={isCreating}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleCreateTopup}
                disabled={isCreating || newTopupAmount < 5}
              >
                {isCreating ? 'Creating...' : `Pay $${newTopupAmount.toFixed(2)}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopupsPage;
